/*
 * Cart drawer features: mutation queue, free-gift reconciler, progress-bar patcher, upsell loader.
 *
 * Loaded globally from sections/header.liquid, after cart.js and cart-drawer.js.
 *
 * EVERYTHING HERE IS A MODULE-LEVEL SINGLETON, ON PURPOSE. cart.js:61 calls replaceWith() on
 * `cart-drawer-items`, which destroys that element and re-runs connectedCallback. Any state stored
 * on an element instance — an in-flight flag, a recursion counter, a fetch cache — would evaporate
 * mid-flight and the guards below would stop guarding.
 *
 * DIVISION OF LABOUR: Liquid decides, JS executes. All threshold maths, currency formatting, gift
 * eligibility and upsell filtering happen server-side in snippets/cart-drawer.liquid and
 * sections/cart-drawer-upsell.liquid, because that is the only place with the settings, the gift
 * variant's price/availability, item.product.gift_card?, the market currency and the `money` filter.
 * This file never recomputes a threshold and never formats money — there is no client-side money
 * formatter in this theme (no currency.js, no Shopify.formatMoney), so hand-rolling comma decimals
 * and symbol placement per market is exactly the bug we are avoiding.
 */

(function () {
  'use strict';

  const GIFT_FAILURE_KEY = 'seepje:giftUnavailable';
  const LAST_ADDED_KEY = 'seepje:lastAddedProductId';
  // Remembers that the customer declined the gift, so the reconciler does not add it straight back.
  const GIFT_DISMISSED_KEY = 'seepje:giftDismissed';

  const drawer = () => document.querySelector('cart-drawer');
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* =========================================================================
   * Mutation queue — strict FIFO for every cart write the drawer performs.
   *
   * A promise queue, not AbortController: aborting a /cart/change.js that already reached Shopify
   * does NOT undo the mutation, it only discards the response, leaving the client's model wrong.
   * Cart writes are not cancellable, so they must be serialised. (AbortController IS used below for
   * the recommendations GET, where cancelling a read is safe.)
   * ====================================================================== */
  const CartMutations = {
    tail: Promise.resolve(),

    run(fn) {
      // `fn` as both handlers so a rejected predecessor still lets the next job run.
      const next = this.tail.then(fn, fn);
      this.tail = next.catch(() => {}); // never poison the chain
      return next;
    },
  };

  /* =========================================================================
   * Progress bars — patch the live nodes, never innerHTML.
   *
   * An innerHTML swap would (a) reset the CSS width transition, because a fresh node starts at its
   * final --p with no previous value to animate from, and (b) replace the aria-live region, making
   * announcements unreliable or duplicated.
   * ====================================================================== */
  const CartProgress = {
    /*
     * How long the "Free shipping unlocked!" line holds the label before it settles back to the next
     * milestone. Long enough to read a short sentence, short enough that the customer is not left
     * wondering what comes next.
     */
    CELEBRATE_MS: 5000,

    /*
     * Celebration state. `celebrateTimer` non-null means the label is currently showing an unlock
     * sentence and does NOT belong to the patcher; `restLabel` holds the real next-milestone HTML
     * that should be restored when the timer fires. Every later patch during a celebration updates
     * restLabel instead of the DOM, so the sentence that lands at restore is the CURRENT one — a
     * customer who keeps clicking + through the celebration must not be shown a stale amount.
     */
    celebrateTimer: null,
    restLabel: null,

    patchFrom(doc) {
      const source = doc.querySelector('cart-progress');
      const target = document.querySelector('cart-progress');

      // The bar can legitimately vanish (cart emptied, non-shop currency, every threshold cleared).
      if (!source) {
        if (target) target.hidden = true;
        // Nothing left to restore the label onto, and a pending timer would write into a hidden bar.
        this.endCelebration(target, { restore: false });
        return;
      }

      /*
       * No bar in the live DOM yet. This is the empty-cart path: snippets/cart-drawer-progress.liquid
       * renders NOTHING when real_item_count is 0, so a visitor who loads the site with an empty cart
       * has no <cart-progress> to patch — and returning early here left them with no progress bar
       * until a full page reload. Insert it instead.
       */
      if (!target) {
        // Clear any stale timer: there is no node left for it to restore onto.
        this.endCelebration(null, { restore: false });
        this.insert(source);
        return;
      }

      target.hidden = false;
      target.dataset.next = source.dataset.next || '';

      /*
       * Segments BEFORE the label now, because the crossing is detected here and decides what the
       * label is allowed to say. Ordering the two DOM writes this way is free — swapping text
       * mid-transition is fine, and only geometry (--seg) must survive untouched.
       */
      const crossed = this.patchSegments(source, target);

      const incomingLabel = source.querySelector('[data-label]');
      const existingLabel = target.querySelector('[data-label]');
      if (!incomingLabel || !existingLabel) return;

      /*
       * Several milestones can fall in one update (a quantity jump, or "add 3 to cart"). Segments are
       * in ascending threshold order, so the LAST crossing is the dearest — announce the biggest win
       * rather than the first one passed.
       */
      const unlocked = crossed.length ? crossed[crossed.length - 1] : '';

      if (unlocked) {
        this.startCelebration(target, existingLabel, incomingLabel.innerHTML, unlocked);
        return;
      }

      // Celebration in flight: bank the newest sentence, leave the label and live region alone.
      if (this.celebrateTimer) {
        this.restLabel = incomingLabel.innerHTML;
        return;
      }

      // innerHTML, not textContent: the copy may contain <strong> around the amount.
      existingLabel.innerHTML = incomingLabel.innerHTML;
      this.announce(source, target);
    },

    /*
     * Take the label over with the unlock sentence, stashing the real one for restore.
     *
     * The sentence is read off the segment's data-unlocked, written by Liquid — JS builds no copy and
     * touches no money, which is the same rule the rest of this file follows.
     *
     * Restarting mid-celebration is deliberate: crossing a second milestone should show the second
     * win, not be swallowed by the first one's remaining time.
     */
    startCelebration(target, labelEl, restHtml, text) {
      if (this.celebrateTimer) clearTimeout(this.celebrateTimer);

      this.restLabel = restHtml;
      labelEl.textContent = text;

      /*
       * Remove/reflow/add rather than a plain add: on a second unlock inside the same celebration the
       * class is already there, and a CSS animation does not replay for a class that never left. The
       * offsetWidth read is the reflow that makes the browser see two separate additions.
       */
      target.classList.remove('cart-progress--celebrating');
      void target.offsetWidth;
      target.classList.add('cart-progress--celebrating');

      /*
       * Announce the win, not the next milestone. Written directly rather than through announce(),
       * which mirrors the source document — and the source has already moved on to the next
       * milestone, so it does not contain this sentence at all.
       */
      const region = target.querySelector('[data-announce]');
      if (region) region.textContent = text;

      this.celebrateTimer = setTimeout(() => {
        this.celebrateTimer = null;
        this.endCelebration(target, { restore: true });
      }, this.CELEBRATE_MS);
    },

    /*
     * Restore deliberately leaves the live region holding the unlock sentence: the screen-reader user
     * has already heard it, and re-announcing the next milestone they were told about a moment ago is
     * noise. announce() dedupes against whatever is in there, so the next genuine change still speaks.
     */
    endCelebration(target, { restore }) {
      if (this.celebrateTimer) {
        clearTimeout(this.celebrateTimer);
        this.celebrateTimer = null;
      }
      if (!target) {
        this.restLabel = null;
        return;
      }

      target.classList.remove('cart-progress--celebrating');

      if (restore && this.restLabel !== null) {
        const labelEl = target.querySelector('[data-label]');
        if (labelEl) labelEl.innerHTML = this.restLabel;
      }
      this.restLabel = null;
    },

    /*
     * First appearance of the bar. Goes between the header and the scroll region, matching the order
     * in snippets/cart-drawer.liquid, and sits outside both re-render selectors so it survives every
     * later mutation.
     *
     * Inserted with the segments empty and filled on the next frame, so the customer sees the bar
     * grow rather than snap to its final width. A clone dropped straight in has no previous value for
     * the browser to animate from.
     */
    insert(source) {
      const panel = document.getElementById('CartDrawer');
      const scroll = panel && panel.querySelector('.cart-drawer__scroll');
      if (!panel || !scroll) return;

      const clone = source.cloneNode(true);
      const segments = Array.from(clone.querySelectorAll('.cart-progress__segment'));
      segments.forEach((seg) => seg.style.setProperty('--seg', '0%'));
      panel.insertBefore(clone, scroll);

      requestAnimationFrame(() => {
        segments.forEach((seg) => {
          const from = source.querySelector(`.cart-progress__segment[data-kind="${seg.dataset.kind}"]`);
          if (from) seg.style.setProperty('--seg', from.style.getPropertyValue('--seg'));
        });
      });
    },

    /*
     * Segments can appear or disappear between renders (the merchant clears a threshold, or the gift
     * becomes unavailable), which changes how many there are. So each is matched by kind and patched
     * in place, and the meter is rebuilt wholesale only when the set of kinds actually differs.
     *
     * Patching in place is the point: writing --seg on the EXISTING fill node gives the browser a
     * previous value to animate from, so the width transition plays. A cloned node would jump.
     *
     * Returns the unlock sentences for milestones crossed BY THIS PATCH, in ascending threshold
     * order. Only a pending -> reached transition counts, so opening the drawer on a cart that
     * already qualifies celebrates nothing — "Free shipping unlocked!" for something unlocked ten
     * minutes ago is noise, and there is no transition to detect on a first render anyway.
     */
    patchSegments(source, target) {
      const incomingMeter = source.querySelector('.cart-progress__meter');
      const existingMeter = target.querySelector('.cart-progress__meter');
      if (!incomingMeter || !existingMeter) return [];

      const kindsOf = (meter) =>
        Array.from(meter.querySelectorAll('.cart-progress__segment'))
          .map((n) => n.dataset.kind)
          .join(',');

      // The set of milestones changed under us, so no before/after pair is comparable.
      if (kindsOf(incomingMeter) !== kindsOf(existingMeter)) {
        existingMeter.innerHTML = incomingMeter.innerHTML;
        return [];
      }

      const crossed = [];

      incomingMeter.querySelectorAll('.cart-progress__segment').forEach((incoming) => {
        const existing = existingMeter.querySelector(`[data-kind="${incoming.dataset.kind}"]`);
        if (!existing) return;

        const previousState = existing.dataset.state;
        existing.dataset.state = incoming.dataset.state;
        // Which segment is currently filling; CSS colours it apart from both done and not-started.
        existing.dataset.active = incoming.dataset.active;
        existing.style.setProperty('--seg', incoming.style.getPropertyValue('--seg'));

        const icon = existing.querySelector('.cart-progress__icon');
        const incomingIcon = incoming.querySelector('.cart-progress__icon');
        if (icon && incomingIcon) icon.title = incomingIcon.title;

        // Pop the icon the moment a threshold is crossed, and hand the sentence up to patchFrom.
        if (previousState === 'pending' && existing.dataset.state === 'reached') {
          crossed.push(incoming.dataset.unlocked || '');

          existing.classList.add('cart-progress__segment--celebrate');
          existing.addEventListener(
            'animationend',
            () => existing.classList.remove('cart-progress__segment--celebrate'),
            { once: true }
          );
        }
      });

      return crossed.filter(Boolean);
    },

    /*
     * Write the live region ONLY when the string actually changed. Otherwise every debounced
     * quantity change re-announces the same sentence, which is intolerable on a screen reader.
     */
    announce(source, target) {
      const incoming = source.querySelector('[data-announce]');
      const existing = target.querySelector('[data-announce]');
      if (!incoming || !existing) return;

      const text = incoming.textContent.trim();
      if (existing.textContent.trim() !== text) existing.textContent = text;
    },
  };

  /* =========================================================================
   * Free gift.
   *
   * Liquid derives a single `action` and hands it over in a JSON island. JS only performs it. That
   * keeps the eligibility rules in one language, and means every wild-cart condition (stale gift,
   * merchant changed the product, merchant disabled the feature, quantity tampered to 2, two gift
   * lines from a lost race, non-shop currency) resolves through the same field.
   * ====================================================================== */
  const GiftReconciler = {
    state: null,
    depth: 0,

    readState(root) {
      const island = (root || document).querySelector('[data-cart-gift-state]');
      if (!island) return null;
      try {
        return JSON.parse(island.textContent);
      } catch (e) {
        console.warn('[cart-drawer] could not parse gift state', e);
        return null;
      }
    },

    /* Called after every render with the freshly rendered document. */
    ingest(doc) {
      const next = this.readState(doc);
      if (!next) return;
      this.state = next;

      // Keep the live island in sync so a later reconcile from a different entry point reads truth.
      const island = document.querySelector('[data-cart-gift-state]');
      const incoming = doc.querySelector('[data-cart-gift-state]');
      if (island && incoming) island.textContent = incoming.textContent;

      this.reconcile();
    },

    reconcile() {
      // Never mutate a merchant's cart from the theme editor: it re-renders sections constantly and
      // the churn is confusing. Precedent: layout/theme.liquid.
      if (window.Shopify && window.Shopify.designMode) return Promise.resolve();

      /*
       * THE LOOP GUARD. Adding or removing the gift is itself a cart mutation, which re-renders the
       * section, which produces a new island, which calls ingest() -> reconcile() again. One user
       * action must produce at most one corrective mutation.
       *
       * The *real* terminator is that Liquid reports action === 'none' once the world is consistent,
       * so the next pass does nothing. This counter only stops a mid-flight re-entry, and refuses to
       * retry when the world is genuinely inconsistent (gift sold out between render and click) —
       * retrying there would hammer /cart/add.js forever.
       */
      if (this.depth > 0) return Promise.resolve();

      const state = this.state || this.readState(document);
      if (!state) return Promise.resolve();

      /*
       * The customer can decline the gift (bin button on the gift line). Without this, the reconciler
       * would see `action: 'add'` on the very next render and put it straight back — the line would
       * appear un-removable.
       *
       * The dismissal is cleared as soon as the cart no longer qualifies, so crossing the threshold
       * again is treated as a fresh offer rather than being suppressed for the whole session.
       */
      if (state.qualifyingTotalCents < state.thresholdCents) {
        this.clearDismissed();
      } else if (state.action === 'add' && this.readDismissed() === String(state.variantId)) {
        return Promise.resolve();
      }

      if (state.action === 'none') return Promise.resolve();

      this.depth += 1;
      return CartMutations.run(() => this.applyAction(state)).finally(() => {
        this.depth = 0;
      });
    },

    applyAction(state) {
      const keys = (state.giftLineKeys || []).filter(Boolean);

      switch (state.action) {
        case 'add':
          return this.addGift(state);
        case 'remove':
          return this.removeLines(keys);
        case 'dedupe':
          // Drop every gift line, then let the next pass re-add exactly one.
          return this.removeLines(keys);
        case 'replace':
          // Stale gift (merchant changed the product). Remove it; the next pass adds the new one.
          return this.removeLines(keys);
        case 'set_qty':
          return this.changeLine(keys[0], 1);
        default:
          return Promise.resolve();
      }
    },

    addGift(state) {
      /*
       * Circuit breaker. Without it, a gift that is permanently unavailable (sold out with
       * inventory_policy: deny, unpublished from the Online Store channel, or outside this market's
       * catalogue) means a failed /cart/add.js on every page load and every cart mutation, forever,
       * for every qualifying customer.
       *
       * Keyed by variant id so changing the setting clears it; sessionStorage so a restock is picked
       * up on the next visit.
       */
      if (this.readFailure() === String(state.variantId)) {
        this.markUnavailable();
        return Promise.resolve();
      }

      /*
       * properties._free_gift is the identifier, not the variant id:
       *  - it survives the merchant changing the gift product, so old gift lines in wild carts stay
       *    identifiable and removable rather than becoming permanent freebies;
       *  - properties participate in the line key, so a customer who legitimately buys the same
       *    product gets their own separate, fully editable line;
       *  - `_`-prefixed properties are hidden from the customer (this theme skips them in
       *    snippets/cart-drawer-item.liquid, and so does Shopify checkout).
       *
       * Deliberately NOT routed through <product-form>: product-form.js:91-112 pushes a GA4
       * add_to_cart unconditionally, and a 0-value add_to_cart per qualifying session would corrupt
       * Seepje's conversion-value and item-level reporting.
       */
      return this.post(window.routes.cart_add_url, {
        items: [{ id: state.variantId, quantity: 1, properties: { _free_gift: 'true' } }],
      }).then((json) => {
        if (!json || json.status) {
          this.writeFailure(state.variantId);
          this.markUnavailable();
          if (json) console.warn('[cart-drawer] free gift could not be added:', json.description || json.message);
          return null;
        }
        this.clearFailure();
        return json;
      });
    },

    removeLines(keys) {
      if (!keys.length) return Promise.resolve();
      // Sequential, by line key: keys are stable across mutations where 1-based indexes are not.
      return keys.reduce(
        (chain, key) => chain.then(() => this.changeLine(key, 0)),
        Promise.resolve()
      );
    },

    changeLine(key, quantity) {
      if (!key) return Promise.resolve();
      return this.post(window.routes.cart_change_url, { id: key, quantity }).then((json) => {
        // A removal that 404s/422s means the line was already gone. That is the desired end state.
        if (json && json.status && quantity === 0) return null;
        return json;
      });
    },

    /*
     * Piggyback the re-render on the mutation (`sections` param) so a gift correction costs one
     * round trip, not two.
     */
    post(url, body) {
      const payload = Object.assign({}, body, {
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname,
      });

      return fetch(url, Object.assign({}, fetchConfig(), { body: JSON.stringify(payload) }))
        .then((response) => response.json())
        .then((json) => {
          if (json && json.sections) this.render(json);
          return json;
        })
        .catch((e) => {
          console.error('[cart-drawer] gift mutation failed', e);
          return null;
        });
    },

    render(json) {
      const el = drawer();
      if (!el) return;

      if (json.sections['cart-drawer']) {
        const doc = parse(json.sections['cart-drawer']);
        // Swap the items + footer, then patch the ancillary parts. Guarded by `depth`, so the
        // ingest() inside patchAncillary cannot start another mutation.
        el.applyDrawerDocument(doc);
      }
      if (json.sections['cart-icon-bubble']) el.renderCartIconBubble(json.sections['cart-icon-bubble']);

      /*
       * Tagged so the CartItems subscriber and our own subscriber below both skip it — mirroring
       * cart.js:33-35. Belt and braces alongside the depth counter.
       */
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift', cartData: json });
    },

    /*
     * The customer met the goal, so do not show an error — a red message in a cart drawer for
     * something they did not do reads as "my order failed" and kills conversion. Show the reward as
     * earned but temporarily unavailable, bar at 100%.
     */
    markUnavailable() {
      const el = drawer();
      if (!el) return;

      const segment = document.querySelector('cart-progress .cart-progress__segment[data-kind="gift"]');
      if (segment) segment.dataset.state = 'unavailable';

      /*
       * Drop any unlock celebration first, WITHOUT restoring: its timer would otherwise fire a second
       * later and overwrite this message with the next-milestone sentence it banked. This message
       * outranks a celebration — the customer needs to know the gift is not coming.
       */
      CartProgress.endCelebration(document.querySelector('cart-progress'), { restore: false });

      // Copy lives on <cart-drawer> as a data attribute — outside every re-render selector, and it
      // keeps the translated string in Liquid where every other string in this feature lives.
      const label = document.querySelector('cart-progress [data-label]');
      if (label && el.dataset.giftUnavailableText) label.textContent = el.dataset.giftUnavailableText;
    },

    readDismissed() {
      try {
        return sessionStorage.getItem(GIFT_DISMISSED_KEY);
      } catch (e) {
        return null;
      }
    },
    writeDismissed(variantId) {
      try {
        sessionStorage.setItem(GIFT_DISMISSED_KEY, String(variantId));
      } catch (e) {}
    },
    clearDismissed() {
      try {
        sessionStorage.removeItem(GIFT_DISMISSED_KEY);
      } catch (e) {}
    },

    readFailure() {
      try {
        return sessionStorage.getItem(GIFT_FAILURE_KEY);
      } catch (e) {
        return null;
      }
    },
    writeFailure(variantId) {
      try {
        sessionStorage.setItem(GIFT_FAILURE_KEY, String(variantId));
      } catch (e) {
        /* private mode — the Liquid-side `available` check is the primary guard anyway */
      }
    },
    clearFailure() {
      try {
        sessionStorage.removeItem(GIFT_FAILURE_KEY);
      } catch (e) {}
    },
  };

  /* =========================================================================
   * Upsells.
   *
   * Deliberately NOT reusing ProductRecommendations from global.js: it calls this.remove() on the
   * `complementary-products` class (global.js:1259), permanently destroying an element the drawer
   * must repopulate on the next open, and it triggers off an IntersectionObserver on itself, which
   * is meaningless for an off-canvas panel.
   * ====================================================================== */
  const CartUpsell = {
    // Cache the PROMISE, keyed by seed only: two near-simultaneous opens then issue one fetch, and a
    // quantity change is never a cache miss.
    cache: new Map(),
    controller: null,
    seed: null,
    // Set while an add originates from inside the drawer, so the seed (and therefore the list) does
    // not change under the customer's finger at the exact moment they are engaging with it.
    suppressNextSeed: false,
    // Recommendations are fetched on FIRST OPEN, never on page load — otherwise every pageview pays
    // for a request whose result most visitors never see.
    hasOpened: false,

    element() {
      return document.querySelector('cart-upsell');
    },

    /* Called after every render. */
    sync(doc, options = {}) {
      const el = this.element();
      if (!el) return;

      // Keep the cart-contents attribute fresh so refilter() can work without a refetch.
      const incoming = doc && doc.querySelector('cart-upsell');
      if (incoming) {
        el.dataset.cartProductIds = incoming.dataset.cartProductIds || '';
        el.dataset.defaultSeed = incoming.dataset.defaultSeed || '';
        el.hidden = incoming.hasAttribute('hidden');
      }

      if (options.seedProductId) {
        if (this.suppressNextSeed) {
          this.suppressNextSeed = false;
        } else {
          this.rememberSeed(options.seedProductId);
        }
      }

      // A product just added disappears from the list without a refetch — the list must not jump
      // under the customer's finger mid-decision.
      this.refilter();

      // Nothing is fetched until the drawer has actually been opened once.
      if (!this.hasOpened) return;

      const seed = this.currentSeed();
      // Refetch only when the seed genuinely changed (an add from outside the drawer). Quantity
      // changes keep the same seed and therefore the same list, so it never jumps.
      if (seed && seed !== this.seed) this.load(seed);
    },

    rememberSeed(productId) {
      try {
        sessionStorage.setItem(LAST_ADDED_KEY, String(productId));
      } catch (e) {}
    },

    /*
     * Seed resolution: the last-added product carries the strongest intent signal and is what
     * Search & Discovery complementary pairs are authored against.
     *  1. sessionStorage, written on every add.
     *  2. data-default-seed = cart.items.last.product.id. Shopify appends new lines at the end, so
     *     this is the server-side proxy for "last added" — free, and it covers both a new session on
     *     a persisted cart and the header-icon open path.
     */
    currentSeed() {
      const el = this.element();
      if (!el) return null;

      const fallback = el.dataset.defaultSeed && el.dataset.defaultSeed !== '0' ? el.dataset.defaultSeed : null;
      if (!fallback) {
        // Cart is empty — drop the stale seed or it survives an emptied cart.
        try {
          sessionStorage.removeItem(LAST_ADDED_KEY);
        } catch (e) {}
        return null;
      }

      let stored = null;
      try {
        stored = sessionStorage.getItem(LAST_ADDED_KEY);
      } catch (e) {}

      return stored || fallback;
    },

    load(seed) {
      const el = this.element();
      if (!el || !seed) return Promise.resolve();

      this.seed = seed;

      if (this.cache.has(seed)) {
        return this.cache.get(seed).then((html) => this.paint(html));
      }

      const promise = this.fetchWithFallback(seed, 'complementary');
      this.cache.set(seed, promise);

      return promise
        .then((html) => this.paint(html))
        .catch((e) => {
          // Do not cache a transient failure forever.
          this.cache.delete(seed);
          if (e && e.name !== 'AbortError') console.error('[cart-drawer] upsell fetch failed', e);
        });
    },

    /*
     * Search & Discovery's curated complementary products first, algorithmic related as the
     * fallback. "Empty" is detected via an explicit data-count attribute, NOT innerHTML.trim():
     * the section emits stylesheet tags before any product markup, so the string is never empty.
     */
    fetchWithFallback(seed, intent) {
      const el = this.element();
      const url = `${el.dataset.url}?section_id=${el.dataset.sectionId}&product_id=${seed}&limit=6&intent=${intent}`;

      // AbortController is safe here: cancelling a read has no side effects, unlike a cart write.
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      return fetch(url, { signal: this.controller.signal })
        .then((response) => response.text())
        .then((text) => {
          const results = parse(text).querySelector('cart-upsell-results');
          const count = Number((results && results.dataset.count) || 0);

          if (count > 0) return text;

          if (intent === 'complementary') {
            // Both "no complementary products configured" (raw-count 0) and "all of them are
            // already in the cart" (raw-count > 0, count 0) fall back — related will differ.
            const raw = Number((results && results.dataset.rawCount) || 0);
            console.debug(`[cart-drawer] no usable complementary upsells (raw=${raw}); trying related`);
            return this.fetchWithFallback(seed, 'related');
          }

          return '';
        });
    },

    paint(html) {
      const el = this.element();
      if (!el) return;

      if (!html) {
        el.innerHTML = '';
        return;
      }

      const results = parse(html).querySelector('cart-upsell-results');
      el.innerHTML = results ? results.innerHTML : '';
      this.refilter();
    },

    /*
     * Client-side re-application of the server-side filters, so an add never needs a refetch.
     * Cards carry data-group-ids for the variant-group siblings: at Seepje, sizes are separate
     * products linked by product.metafields.custom.variant_group, so without this a customer holding
     * the 1 L would be upsold the 500 ml of the same product.
     */
    refilter() {
      const el = this.element();
      if (!el) return;

      const inCart = new Set((el.dataset.cartProductIds || '').split(',').filter(Boolean));
      let visible = 0;

      el.querySelectorAll('[data-upsell-product-id]').forEach((card) => {
        const id = card.dataset.upsellProductId;
        const groupIds = (card.dataset.groupIds || '').split(',').filter(Boolean);
        const clash = inCart.has(id) || groupIds.some((groupId) => inCart.has(groupId));

        card.hidden = clash;
        if (!clash) visible += 1;
      });

      const heading = el.querySelector('.cart-upsell__heading');
      if (heading) heading.hidden = visible === 0;
    },
  };

  /* =========================================================================
   * Wiring
   * ====================================================================== */

  window.CartProgress = CartProgress;
  window.GiftReconciler = GiftReconciler;
  window.CartUpsell = CartUpsell;
  window.CartMutations = CartMutations;

  /*
   * The quantity-change path. cart.js#updateQuantity swaps `.js-contents` and `.cart-drawer__footer`
   * itself and then publishes cartUpdate with the full response, so we only need to patch the
   * ancillary parts from the same payload — no extra request.
   */
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    // Our own mutation — the depth counter already covers re-entry, this is the second guard.
    if (event.source === 'free-gift') return;
    // product-form.js publishes BEFORE calling renderContents(), so anything done here would be
    // immediately overwritten. renderContents handles that path.
    if (event.source === 'product-form') return;

    const html = event.cartData && event.cartData.sections && event.cartData.sections['cart-drawer'];
    const el = drawer();
    if (!html || !el) return;

    el.patchAncillary(parse(html));
  });

  document.addEventListener('DOMContentLoaded', () => {
    if (!drawer()) return;

    GiftReconciler.state = GiftReconciler.readState(document);
    CartUpsell.sync(document);

    /*
     * The `action === 'none'` early-out inside reconcile() is what makes this free: without it every
     * single page view would issue a cart write. Deferred to idle so it never competes with paint.
     */
    const kick = () => GiftReconciler.reconcile();
    if (window.requestIdleCallback) {
      window.requestIdleCallback(kick, { timeout: 2000 });
    } else {
      setTimeout(kick, 0);
    }
  });

  // First open is what triggers the recommendations fetch. Opening also reveals a cart that may have
  // been mutated in another tab, so reconcile the gift here too.
  document.addEventListener('cartDrawer:open', () => {
    CartUpsell.hasOpened = true;
    CartUpsell.sync(document);
    GiftReconciler.reconcile();
  });

  /*
   * Declining the gift.
   *
   * CAPTURE PHASE so this runs before <cart-remove-button>'s own bubble handler (cart.js:5-9), which
   * would fire dataLayerEventRemove — we keep the 0-value gift line out of GA4, exactly as the add
   * path does by not routing through <product-form>.
   *
   * The dismissal must be recorded BEFORE the mutation: the removal triggers a re-render, whose
   * island still says the cart qualifies, so the reconciler would otherwise add the gift straight
   * back and the bin would look broken.
   */
  document.addEventListener(
    'click',
    (event) => {
      const btn = event.target.closest('[data-gift-remove]');
      if (!btn) return;

      const items = btn.closest('cart-drawer-items');
      const el = drawer();
      if (!items || !el) return;

      event.preventDefault();
      event.stopPropagation();

      GiftReconciler.writeDismissed(el.dataset.giftVariantId || '0');
      items.updateQuantity(btn.dataset.index, 0);
    },
    true
  );

  /*
   * Minus at the minimum quantity removes the line, instead of doing nothing.
   *
   * QuantityInput.onButtonClick (global.js) calls input.stepDown(), which clamps at the `min`
   * attribute — so at quantity 1 the value never changes, no `change` event fires, and the only way
   * to remove an item was the bin. This intercepts that case.
   *
   * CAPTURE PHASE on document, so it runs before the event reaches <quantity-input> and its own
   * bubble-phase handler. stopPropagation() here prevents the descent entirely, so stepDown never
   * runs and we don't fight it.
   *
   * Delegated rather than bound per button because every line is re-rendered on each cart mutation.
   */
  document.addEventListener(
    'click',
    (event) => {
      const minus = event.target.closest('.cart-drawer__quantity .quantity__button[name="minus"]');
      if (!minus) return;

      const input = minus.closest('quantity-input')?.querySelector('.quantity__input');
      if (!input) return;

      const value = parseInt(input.value, 10);

      /*
       * Remove ONLY on the 1 -> 0 step. Anything else is an ordinary decrement and must leave the
       * line in the cart — global.js handles it untouched.
       *
       * This previously compared against the input's `min` attribute, which comes from
       * item.variant.quantity_rule.min. For any product with a quantity rule above 1 that made the
       * FIRST press of minus delete the whole line (at qty 6 with min 6, `value > min` is false), so
       * decrementing looked like the product vanishing. A stale or empty value parsed to NaN could
       * reach the same branch.
       *
       * Guarding on `value === 1` means the only way to remove by minus is the step the user
       * explicitly asked for. A quantity-rule floor above 1 now just clamps, and the bin button
       * remains the way to remove such a line outright.
       */
      if (!Number.isFinite(value) || value !== 1) return;

      const items = minus.closest('cart-drawer-items');
      if (!items) return;

      event.preventDefault();
      event.stopPropagation();

      // Same two calls, in the same order, as cart-remove-button (cart.js:5-9) — so removing via
      // minus reports an identical GA4 remove_from_cart event to removing via the bin.
      const index = input.dataset.index;
      items.dataLayerEventRemove(index);
      items.updateQuantity(index, 0);
    },
    true
  );

  /*
   * An add from inside the drawer must not re-seed the upsell list. Captured on the drawer (the
   * upsell forms live inside it and are replaced on every render, so per-form binding would break).
   */
  document.addEventListener(
    'submit',
    (event) => {
      if (event.target.closest && event.target.closest('.cart-upsell__form')) {
        CartUpsell.suppressNextSeed = true;
      }
    },
    true
  );
})();
